const CACHE_KEY = "quotation-cache-by-year";
const CACHE_TTL_MS = 2 * 60 * 1000;

let memoryCache = {};

const isBrowser = typeof window !== "undefined";

const readStorageCache = () => {
  if (!isBrowser) return {};

  try {
    const raw = window.sessionStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    console.error("Failed to read quotation cache:", error);
    return {};
  }
};

const writeStorageCache = (cache) => {
  if (!isBrowser) return;

  try {
    window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.error("Failed to write quotation cache:", error);
  }
};

const getMergedCache = () => {
  const storageCache = readStorageCache();
  memoryCache = { ...storageCache, ...memoryCache };
  return memoryCache;
};

export const clearQuotationCache = () => {
  memoryCache = {};

  if (!isBrowser) return;

  try {
    window.sessionStorage.removeItem(CACHE_KEY);
  } catch (error) {
    console.error("Failed to clear quotation cache:", error);
  }
};

export const getCachedQuotations = (year) => {
  const cache = getMergedCache();
  const entry = cache[String(year)];

  if (!entry) return null;

  if (Date.now() - entry.cachedAt > CACHE_TTL_MS) {
    delete cache[String(year)];
    memoryCache = cache;
    writeStorageCache(cache);
    return null;
  }

  return Array.isArray(entry.data) ? entry.data : null;
};

export const setCachedQuotations = (year, quotations) => {
  const cache = getMergedCache();
  cache[String(year)] = {
    data: Array.isArray(quotations) ? quotations : [],
    cachedAt: Date.now(),
  };

  memoryCache = cache;
  writeStorageCache(cache);
};

export const patchCachedQuotations = (year, updater) => {
  const current = getCachedQuotations(year);
  if (!current) return;

  const next = typeof updater === "function" ? updater(current) : current;
  setCachedQuotations(year, next);
};

export const getQuotationCacheYear = (quotation) => {
  const dateValue = quotation?.documentDate || quotation?.createdAt;
  const date = dateValue ? new Date(dateValue) : null;

  return date && !Number.isNaN(date.getTime())
    ? date.getFullYear()
    : new Date().getFullYear();
};

export const findCachedQuotationById = (quotationId) => {
  const cache = getMergedCache();
  let hasRemovedExpiredEntry = false;

  for (const [yearKey, entry] of Object.entries(cache)) {
    if (!entry || !Array.isArray(entry.data)) continue;

    if (Date.now() - Number(entry.cachedAt || 0) > CACHE_TTL_MS) {
      delete cache[yearKey];
      hasRemovedExpiredEntry = true;
      continue;
    }

    const matchedQuotation = entry.data.find((item) => item?._id === quotationId);
    if (matchedQuotation) {
      if (hasRemovedExpiredEntry) {
        memoryCache = cache;
        writeStorageCache(cache);
      }
      return matchedQuotation;
    }
  }

  if (hasRemovedExpiredEntry) {
    memoryCache = cache;
    writeStorageCache(cache);
  }

  return null;
};

export const upsertCachedQuotation = (quotation) => {
  if (!quotation?._id) return;

  const cache = getMergedCache();
  const yearKeyToInsert = String(getQuotationCacheYear(quotation));
  const hasKnownYearCache = Boolean(cache[yearKeyToInsert]);
  let hasRemovedExistingQuotation = false;
  let hasUpdatedCachedQuotation = false;

  Object.keys(cache).forEach((yearKey) => {
    const entry = cache[yearKey];
    if (!entry || !Array.isArray(entry.data)) return;

    // Keep an existing quotation at the same position as the API response.
    if (yearKey === yearKeyToInsert) {
      const quotationIndex = entry.data.findIndex(
        (item) => item?._id === quotation._id,
      );

      if (quotationIndex === -1) return;

      const nextData = [...entry.data];
      nextData[quotationIndex] = {
        ...nextData[quotationIndex],
        ...quotation,
      };
      cache[yearKey] = {
        ...entry,
        data: nextData,
        cachedAt: Date.now(),
      };
      hasUpdatedCachedQuotation = true;
      return;
    }

    const nextData = entry.data.filter((item) => item?._id !== quotation._id);
    if (nextData.length !== entry.data.length) {
      hasRemovedExistingQuotation = true;
    }

    cache[yearKey] = {
      ...entry,
      data: nextData,
      cachedAt: Date.now(),
    };
  });

  if (!hasKnownYearCache) {
    if (hasRemovedExistingQuotation) {
      memoryCache = cache;
      writeStorageCache(cache);
    }
    return;
  }

  if (hasUpdatedCachedQuotation) {
    memoryCache = cache;
    writeStorageCache(cache);
    return;
  }

  const targetEntry = cache[yearKeyToInsert] || { data: [] };
  cache[yearKeyToInsert] = {
    ...targetEntry,
    // New quotations are inserted by date; existing API ordering remains intact.
    data: [...(targetEntry.data || []), { ...quotation }].sort((a, b) => {
      const aDate = new Date(a?.documentDate || a?.createdAt || 0).getTime();
      const bDate = new Date(b?.documentDate || b?.createdAt || 0).getTime();
      return bDate - aDate;
    }),
    cachedAt: Date.now(),
  };

  memoryCache = cache;
  writeStorageCache(cache);
};

export const removeCachedQuotation = (quotationId) => {
  if (!quotationId) return;

  const cache = getMergedCache();
  let hasUpdated = false;

  Object.keys(cache).forEach((yearKey) => {
    const entry = cache[yearKey];
    if (!entry || !Array.isArray(entry.data)) return;

    const nextData = entry.data.filter((item) => item?._id !== quotationId);
    if (nextData.length === entry.data.length) return;

    cache[yearKey] = {
      ...entry,
      data: nextData,
      cachedAt: Date.now(),
    };
    hasUpdated = true;
  });

  if (!hasUpdated) return;

  memoryCache = cache;
  writeStorageCache(cache);
};
